// ============================================================================
// BREAK-GLASS TOGGLE
// A System Administrator's button to start break-glass for a scope on demand,
// rather than waiting for a 403. Renders nothing for other users or while the
// scope already has a grant (the page's banner then shows it, with End).
// ============================================================================

import React, { useState } from 'react';
import { Lock, ShieldAlert } from 'lucide-react';
import { BreakGlassModal } from '../common/BreakGlassModal';
import { useBreakGlass, useIsSystemAdmin } from '../../contexts/BreakGlassContext';
import { BreakGlassContext, BreakGlassScope, GrantOptions } from '../../services/breakGlass';

interface BreakGlassToggleProps {
  scope: BreakGlassScope | null;
  /** Finishes "You are about to …" in the prompt */
  action: string;
  label: string;
  grantOptions?: GrantOptions;
  note?: string;
  /** chip: filter bars; button: light page headers; dark: navy headers */
  variant?: 'chip' | 'button' | 'dark';
  onStart?: (ctx: BreakGlassContext) => void;
}

const STYLES = {
  chip: 'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-amber-600 hover:bg-amber-50 transition-colors border border-transparent hover:border-amber-200',
  button: 'flex items-center justify-center gap-2 rounded-xl h-10 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-bold hover:bg-slate-50 shadow-sm transition-colors',
  dark: 'flex items-center gap-1.5 rounded-lg h-8 px-3 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 hover:text-amber-200 border border-amber-400/30 text-xs font-bold transition-colors',
};

export const BreakGlassToggle: React.FC<BreakGlassToggleProps> = ({
  scope, action, label, grantOptions, note, variant = 'button', onStart,
}) => {
  const isSystemAdmin = useIsSystemAdmin();
  const { active, start } = useBreakGlass(isSystemAdmin ? scope : null);
  const [open, setOpen] = useState(false);

  if (!isSystemAdmin || !scope || active) return null;

  const icon = variant === 'chip'
    ? <Lock size={11} />
    : <ShieldAlert size={variant === 'dark' ? 14 : 16} className={variant === 'button' ? 'text-amber-500' : undefined} />;

  return (
    <>
      <button onClick={() => setOpen(true)} className={STYLES[variant]} data-testid="break-glass-toggle">
        {icon} {label}
      </button>
      <BreakGlassModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onConfirm={(ctx) => {
          start(ctx, grantOptions);
          setOpen(false);
          onStart?.(ctx);
        }}
        action={action}
        note={note}
      />
    </>
  );
};
